import { IconButton, LuIcon, Text, toast, useAlertDialog } from "@overmap-ai/blocks"
import { OrganizationAccessLevel, type Project, type User } from "@overmap-ai/common"
import { APIError, selectAccessesByUserOfProject } from "@overmap-ai/core"
import {
	type ColumnDef,
	getCoreRowModel,
	getSortedRowModel,
	type TableOptions,
	useReactTable,
} from "@tanstack/react-table"
import { useCallback, useMemo } from "react"

import { modelIconMapping } from "../../../mappings/icons"
import { sdk } from "../../../sdk"
import { useAppSelector } from "../../../store"
import { formatTimestampDate } from "../../../utils/date"
import { UserIcon } from "../../Icons/UserIcon"
import { OrganizationAccessRequired } from "../../OrganizationAccessRequired/AccessRequired"
import { ColumnSortButton, ColumnSortIcon } from "../components"

export const useProjectUserTableColumns = (project: Project) => {
	const projectAccessesByUser = useAppSelector(selectAccessesByUserOfProject(project.uuid))

	return useMemo<ColumnDef<User>[]>(
		() => [
			{
				id: "userAvatar",
				enableHiding: false,
				cell({ row }) {
					return <UserIcon userId={row.original?.id} />
				},
				enableSorting: false,
				meta: {
					plainHeader: "Avatar",
				},
			},
			{
				id: "email",
				accessorKey: "email",
				enableHiding: false,
				meta: {
					plainHeader: "Email",
				},
				header({ column }) {
					return (
						<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
							Email
							<ColumnSortIcon sortDirection={column.getIsSorted()} />
						</ColumnSortButton>
					)
				},
				cell: ({ getValue }) => {
					const email = getValue<string>()
					return <Text accentColor="base">{email}</Text>
				},
			},
			{
				id: "created_at",
				enableHiding: false,
				accessorFn: (row) => projectAccessesByUser[row.id].created_at,
				header({ column }) {
					return (
						<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
							Joined
							<ColumnSortIcon sortDirection={column.getIsSorted()} />
						</ColumnSortButton>
					)
				},
				cell: ({ getValue }) => {
					return (
						<Text accentColor="base" className="whitespace-nowrap">
							{formatTimestampDate(getValue<string>())}
						</Text>
					)
				},
			},
			{
				id: "removeUser",
				enableHiding: false,
				cell: function Cell({ row }) {
					const user = row.original
					const openAlertDialog = useAlertDialog()

					const handleRemoveClick = useCallback(() => {
						openAlertDialog({
							title: "Remove user",
							description:
								"This will remove the user from the project. You can invite them back at any time.",
							size: "sm",
							onAction: () => {
								sdk.projectAccesses
									.delete(projectAccessesByUser[user.id].uuid)
									.catch((error: unknown) => {
										toast.error({
											title: "Unable to remove user",
											description:
												error instanceof APIError
													? error.message
													: "An unexpected error occurred",
										})
									})
							},
							action: "Remove",
						})
					}, [openAlertDialog, user.id])

					return (
						<OrganizationAccessRequired accessLevel={OrganizationAccessLevel.ADMIN} type="disable">
							<IconButton onClick={handleRemoveClick} variant="ghost" accentColor="base">
								<LuIcon icon={modelIconMapping.close} />
							</IconButton>
						</OrganizationAccessRequired>
					)
				},
				enableSorting: false,
			},
		],
		[projectAccessesByUser],
	)
}

export const useProjectUserTable = (args: Omit<TableOptions<User>, "getCoreRowModel">) => {
	return useReactTable<User>({
		...args,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
	})
}
